import { unparse } from 'papaparse'
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import {
  requestPlans,
  requestAllPlans,
  clearAllPlans
} from '@/actions/operations/plan'

import { requestAllGrades } from '@/actions/operations/grade'

import {
  getCurrentUser,
  getCurrentUserOrganizations,
  getZonesHierarchy,
  getOperationsPlans,
  getOperationsPlansCount,
  getOperationsAllPlans,
  getOperationsAllGrades,
  getOperationsLoading
} from '@/reducers/selectors'

import {
  Flex,
  Box,
  Table,
  Slot,
  Column,
  Row,
  Text,
  Loader,
  Button
} from '@/primitives'

import { Pagination } from '@/elements'

import HeaderV2 from '@/components/Operations/Shared/HeaderV2'
import TableFilter from './TableFilter'
import TableRow from './TableRow'

import { generateDownloadCsvLink } from '@/Util/GeneralUtils'
import { DOWNLOAD_HEADERS, DOWNLOAD_VALUES } from './config'

import history from '../../../../../history'

import {
  hasEditPermissions,
  RESOURCE_TYPE_OPERATIONS,
  RESOURCE_TYPE_OPERATIONS_PLANS
} from '@/Util/PermissionUtils'

import Strings from '../Strings'

function PlanTable({ state, dispatchState, modulePath, backPath }) {
  const dispatch = useDispatch()

  const currentUser = getCurrentUser()
  const organizations = getCurrentUserOrganizations()
  const zonesHierarchy = getZonesHierarchy()
  const items = getOperationsPlans()
  const totalItems = getOperationsPlansCount()
  const allItems = getOperationsAllPlans()
  const grades = getOperationsAllGrades()
  const loading = getOperationsLoading()

  const strings = Strings()

  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(9)
  const [tableState, setTableState] = useState(state)
  const [canEdit, setCanEdit] = useState(false)
  const [requestDownload, setRequestDownload] = useState(false)

  useEffect(() => {
    if (currentUser.userName) {
      setCanEdit(
        hasEditPermissions(
          currentUser,
          RESOURCE_TYPE_OPERATIONS,
          RESOURCE_TYPE_OPERATIONS_PLANS
        )
      )
    }
  }, [currentUser])

  useEffect(() => {
    dispatch(requestAllGrades())
  }, [dispatch])

  useEffect(() => {
    if (tableState.page !== state.page || items.length === 0) {
      setTableState({ ...state })
      fetchPlans(state.page)
    }
  }, [state.page])

  useEffect(() => {
    if (
      tableState.organizations !== state.organizations ||
      tableState.sites !== state.sites
    ) {
      setTableState({ ...state })
      fetchPlans(1)
      changePage(1)
    }
  }, [state.organizations, state.sites])

  useEffect(() => {
    if (requestDownload) {
      dispatch(
        requestAllPlans({
          filter: {
            organizationId: state.organizations,
            siteId: state.sites
          }
        })
      )
    }
  }, [requestDownload])

  useEffect(() => {
    if (requestDownload && allItems.length > 0) {
      const headers = DOWNLOAD_HEADERS(grades)
      const data = [headers]
      for (const item of allItems) {
        const values = DOWNLOAD_VALUES(
          item,
          organizations,
          zonesHierarchy,
          grades
        )
        data.push(values)
      }
      const csv = unparse({ data })
      generateDownloadCsvLink(csv, 'plans.csv')
      dispatch(clearAllPlans())
      setRequestDownload(false)
    }
  }, [allItems])

  const fetchPlans = page => {
    if (state.organizations.length > 0 && state.sites.length > 0) {
      dispatch(
        requestPlans({
          filter: {
            organizationId: state.organizations,
            siteId: state.sites
          },
          limit: pageSize,
          offset: page - 1
        })
      )
    }
  }

  const changePage = newPage => {
    dispatchState({
      page: newPage
    })
  }

  const onCreate = () => {
    history.push(`${modulePath}/create`)
  }

  const onEdit = id => {
    history.push(`${modulePath}/update/${id}`)
  }

  const onDownload = () => {
    setRequestDownload(true)
  }

  return (
    <Flex axisGap={400} direction='column'>
      <HeaderV2
        title={strings.managePlans}
        backPath={backPath}
        buttonText={strings.createPlan}
        buttonCallback={onCreate}
        showButton={canEdit}
      >
        <TableFilter state={state} dispatchState={dispatchState} />
        <Button
          size='small'
          iconBefore={'download'}
          variant='info'
          name={'downloadButton'}
          onClick={onDownload}
        >
          {strings.download}
        </Button>
      </HeaderV2>
      <Loader isLoading={loading}>
        {totalItems === 0 && (
          <Flex className={'Operations__Table__Empty'}>
            <Text>{strings.noPlans}</Text>
          </Flex>
        )}
        {totalItems > 0 && (
          <Table className='Operations__Table' aria-colcount='5'>
            <Slot name='head'>
              <Row>
                <Column>{strings.date}</Column>
                <Column>{strings.zone}</Column>
                <Column>{strings.produce}</Column>
                <Column>{strings.weight}</Column>
                <Column />
              </Row>
            </Slot>
            <Slot name='body'>
              {items?.map(item => (
                <TableRow
                  key={item.id}
                  onEdit={onEdit}
                  canEdit={canEdit}
                  item={item}
                />
              ))}
            </Slot>
          </Table>
        )}
        {totalItems > pageSize && (
          <Box className='pagination-menu'>
            <Pagination
              type='simple'
              totalItems={totalItems}
              totalPages={totalPages}
              setTotalPages={setTotalPages}
              page={tableState.page}
              setPage={changePage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </Box>
        )}
      </Loader>
    </Flex>
  )
}

export default PlanTable
